import React from "react";
import { Card, Col, Row, Tooltip } from "antd";
import {
  CalendarOutlined,
  FallOutlined,
  HourglassOutlined,
  RiseOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  PnlMonthColumn,
  PnlTopSheetData,
  fmtINR,
  fmtINRCompact,
  fmtMonth,
  num,
} from "./pnlShared";

// KPI cards, style copied from MaterialKpiCards.tsx — compact ₹ value with a
// Tooltip carrying the exact fmtINR amount.

const valueStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: "#1a1a2e",
  marginTop: 6,
  fontVariantNumeric: "tabular-nums",
  cursor: "default",
  width: "fit-content",
};

const titleStyle = (color: string): React.CSSProperties => ({
  fontSize: 12.5,
  fontWeight: 600,
  color,
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const cardStyle = (background: string, border: string): React.CSSProperties => ({
  borderRadius: 12,
  background,
  border: `1px solid ${border}`,
  height: "100%",
});

const subStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#8c8c8c",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const PnlKpiCards: React.FC<{
  data?: PnlTopSheetData | null;
}> = ({ data }) => {
  const months: PnlMonthColumn[] = data?.months || [];
  const totals = data?.totals;
  const totalInflow = num(totals?.totalInflow);
  const totalOutflow = num(totals?.totalOutflow);
  const actualOutflow = num(totals?.actualOutflow);
  const margin = num(totals?.margin);
  const marginPct = num(totals?.marginPct);
  const marginPositive = margin >= 0;

  const peak = months.reduce(
    (best: PnlMonthColumn | null, column) =>
      !best || num(column.totalOutflow) > num(best.totalOutflow) ? column : best,
    null
  );

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} xl={5}>
        <Card size="small" style={cardStyle("#f0f7ff", "#bae0ff")} bodyStyle={{ padding: "12px 16px" }}>
          <div style={titleStyle("#1677ff")}>
            <WalletOutlined /> Total Inflow
          </div>
          <Tooltip title={fmtINR(totalInflow)}>
            <div style={valueStyle}>{fmtINRCompact(totalInflow)}</div>
          </Tooltip>
          <div style={subStyle}>Work bill (scheduled qty × rate)</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={5}>
        <Card size="small" style={cardStyle("#fff7e6", "#ffd591")} bodyStyle={{ padding: "12px 16px" }}>
          <div style={titleStyle("#fa8c16")}>
            <FallOutlined /> Total Outflow (Planned)
          </div>
          <Tooltip title={fmtINR(totalOutflow)}>
            <div style={valueStyle}>{fmtINRCompact(totalOutflow)}</div>
          </Tooltip>
          <div style={subStyle}>Material + Manpower + Machinery</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={5}>
        <Card size="small" style={cardStyle("#fafafa", "#e8e8e8")} bodyStyle={{ padding: "12px 16px" }}>
          <div style={titleStyle("#8c8c8c")}>
            <HourglassOutlined /> Total Outflow (Actual)
          </div>
          <Tooltip title={fmtINR(actualOutflow)}>
            <div style={{ ...valueStyle, color: "#8c94a8" }}>
              {fmtINRCompact(actualOutflow)}
            </div>
          </Tooltip>
          <div style={subStyle}>Actuals feed pending (DPR / consumption)</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={5}>
        <Card
          size="small"
          style={cardStyle(marginPositive ? "#f6ffed" : "#fff1f0", marginPositive ? "#b7eb8f" : "#ffa39e")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle(marginPositive ? "#389e0d" : "#cf1322")}>
            <RiseOutlined /> Margin
          </div>
          <Tooltip title={fmtINR(margin)}>
            <div style={{ ...valueStyle, color: marginPositive ? "#237804" : "#cf1322" }}>
              {fmtINRCompact(margin)}
            </div>
          </Tooltip>
          <div style={subStyle}>{marginPct.toFixed(1)}% of inflow</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={4}>
        <Card size="small" style={cardStyle("#f9f4ff", "#d3adf7")} bodyStyle={{ padding: "12px 16px" }}>
          <div style={titleStyle("#722ed1")}>
            <CalendarOutlined /> Peak Outflow
          </div>
          {peak ? (
            <>
              <Tooltip title={fmtINR(peak.totalOutflow)}>
                <div style={valueStyle}>{fmtINRCompact(peak.totalOutflow)}</div>
              </Tooltip>
              <div style={subStyle}>
                {fmtMonth(peak.month)} · {months.length} month{months.length === 1 ? "" : "s"} scheduled
              </div>
            </>
          ) : (
            <>
              <div style={valueStyle}>—</div>
              <div style={subStyle}>No scheduled months</div>
            </>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default PnlKpiCards;
